import { workspaceOperationSchema } from '@mio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ApiRequestError } from '@/shared/api/api-error';
import { apiRequest } from '@/shared/api/client';
import { homeSummaryQuery } from '@/shared/api/queries/home';
import { programKeys } from '@/shared/api/queries/program';
import { workspaceQuery } from '@/shared/api/queries/workspace';
import { operationQueryKey } from '@/shared/api/wait-for-workspace-operation';

import {
  commandPath,
  editorSessionFor,
  readPendingCommand,
  storePendingCommand,
} from './workspace-session';

import type { PendingCommand } from './workspace-session';
import type { CurrentWorkspace, WorkspaceOperation } from '@mio/shared';

export const operationFinished = (operation: WorkspaceOperation | null | undefined) =>
  operation?.status === 'succeeded' || operation?.status === 'failed';
const operationKey = (id: string | null) =>
  id ? operationQueryKey(id) : ['workshop', 'operation', null];

/** A transport retry reuses frozen input. A terminal job retry is a new user command. */
export function useWorkspaceCommands(current: CurrentWorkspace | undefined) {
  const client = useQueryClient();
  const workspaceId = current?.workspace?.id ?? null;
  const [stored, setStored] = useState<{
    workspaceId: string | null;
    request: PendingCommand | null;
  }>({ workspaceId: null, request: null });
  const request = stored.workspaceId === workspaceId ? stored.request : null;
  const requestRef = useRef(request);
  requestRef.current = request;
  const sending = useRef(false);
  const handled = useRef<string | null>(null);
  const [observed, setObserved] = useState<{ workspaceId: string; id: string } | null>(null);
  const serverOperationId = current?.busyOperation?.id;
  useEffect(() => {
    if (workspaceId && serverOperationId) setObserved({ workspaceId, id: serverOperationId });
  }, [workspaceId, serverOperationId]);
  useEffect(() => {
    setStored({ workspaceId, request: workspaceId ? readPendingCommand(workspaceId) : null });
  }, [workspaceId]);
  const update = (value: PendingCommand | null) => {
    requestRef.current = value;
    setStored({ workspaceId, request: value });
    if (workspaceId) storePendingCommand(workspaceId, value);
  };
  const mutation = useMutation({
    mutationFn: async (value: PendingCommand) => {
      sending.current = true;
      try {
        return await apiRequest(commandPath(value), workspaceOperationSchema, {
          method: 'POST',
          body: value.command,
        });
      } finally {
        sending.current = false;
      }
    },
    onSuccess: (result, value) => {
      client.setQueryData(operationKey(result.id), result);
      if (workspaceId) setObserved({ workspaceId, id: result.id });
      update({ ...value, jobId: result.id });
      void client.invalidateQueries({ queryKey: workspaceQuery.queryKey });
    },
    onError: (error) => {
      // A lost response or 5xx may follow a committed transaction; preserve the key.
      if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500)
        update(null);
      void client.invalidateQueries({ queryKey: workspaceQuery.queryKey });
    },
  });
  // The current-state poll drops completed jobs before this poll necessarily sees
  // their result. Retain the last observed ID so background failures stay visible.
  // A new command has no progress to display until its receipt or the server poll
  // identifies it; the previous job's terminal state does not belong to it.
  const awaitingResponse = Boolean(request && !request.jobId);
  const id =
    serverOperationId ??
    (awaitingResponse
      ? null
      : ((observed?.workspaceId === workspaceId ? observed?.id : null) ?? request?.jobId ?? null));
  const operation = useQuery({
    queryKey: operationKey(id),
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      apiRequest(`/workshop/operations/${id}`, workspaceOperationSchema, { signal }),
    refetchInterval: (query) => (operationFinished(query.state.data) ? false : 1500),
    // A learner returning to the tab sees the settled result at once instead of
    // waiting for the next interval after background throttling.
    refetchOnWindowFocus: true,
    retry: false,
  });
  const result = operation.data ?? current?.busyOperation ?? null;
  useEffect(() => {
    if (!result || !operationFinished(result)) return;
    // Completion retires the pending request, not the observed result. Keeping
    // an old request in memory would block new work once its query is collected.
    // Run this even for a handled result: a delayed POST receipt may arrive after
    // the status query has already observed that same job's completion.
    if (workspaceId && requestRef.current?.jobId === result.id) {
      requestRef.current = null;
      setStored((previous) =>
        previous.workspaceId === workspaceId && previous.request?.jobId === result.id
          ? { workspaceId, request: null }
          : previous,
      );
      if (readPendingCommand(workspaceId)?.jobId === result.id)
        storePendingCommand(workspaceId, null);
    }
    if (handled.current === result.id) return;
    handled.current = result.id;
    void client.invalidateQueries({ queryKey: workspaceQuery.queryKey });
    void client.invalidateQueries({ queryKey: ['workshop', 'session'] });
    void client.invalidateQueries({ queryKey: ['workshop', 'history'] });
    // Only a switch or a submission changes program progress; a save or pause
    // must not refetch the whole curriculum.
    if (result.action === 'activate' || result.action === 'submit') {
      void client.invalidateQueries({ queryKey: programKeys.full() });
      void client.invalidateQueries({ queryKey: homeSummaryQuery.queryKey });
    }
  }, [client, result, request?.jobId, workspaceId]);
  // A normal in-flight request is not a lost response. Restored requests and
  // transport failures still need an explicit retry with their original key.
  const uncertain = awaitingResponse && !mutation.isPending && !sending.current;
  const busy =
    mutation.isPending ||
    awaitingResponse ||
    Boolean(id && !operationFinished(result)) ||
    Boolean(current?.busyOperation);

  async function execute(action: PendingCommand['action'], activityId: string | null = null) {
    const workspace = current?.workspace;
    if (!workspace || stored.workspaceId !== workspaceId)
      throw new Error('Workspace status is still loading.');
    const pending = requestRef.current;
    const pendingResult = pending?.jobId
      ? client.getQueryData<WorkspaceOperation>(operationKey(pending.jobId))
      : null;
    if (busy || sending.current || (pending && !operationFinished(pendingResult)))
      throw new Error('Wait for the current workspace operation to finish.');
    sending.current = true;
    try {
      const next: PendingCommand = {
        action,
        activityId,
        jobId: null,
        command: {
          idempotencyKey: crypto.randomUUID(),
          expectedGeneration: workspace.generation,
          expectedAttemptId: workspace.activeAttemptId,
          editorSessionId: await editorSessionFor(workspace),
        },
      };
      update(next);
      return await mutation.mutateAsync(next);
    } finally {
      sending.current = false;
    }
  }
  async function retryRequest() {
    if (!request || request.jobId || sending.current) return;
    return mutation.mutateAsync(request);
  }
  return {
    execute,
    retryRequest,
    busy,
    uncertain,
    initialized: stored.workspaceId === workspaceId,
    operation: result,
    action: request && (!request.jobId || request.jobId === result?.id) ? request.action : null,
    requestError: mutation.error,
    statusError: operation.error,
    retryStatus: operation.refetch,
  };
}
